// @flow
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { reduce, map, filter, propEq, complement, when, over, lensPath, inc, dec } from 'ramda';
import { useTranslation } from 'react-i18next';

import type { ComplexMap, Complex } from 'app/common/components/Cafeteria/dataTypes/order/Complex.types';
import type { OrderSettings } from 'app/common/components/Cafeteria/dataTypes/order/OrderSettings.types';
import type { OrderItemMap } from 'app/common/components/Cafeteria/dataTypes/order/OrderItem.types';
import createOrderItem from 'app/common/components/Cafeteria/dataTypes/order/createOrderItem';

import type { Available } from '../../../../dataTypes/orderRequests/Available.types';
import { groupTypes, type GroupType } from '../../../../dataTypes/orderRequests/groupTypes';
import getIsSourceItemDisabled from './getIsSourceItemDisabled';


type State = {
  complexes: Array<Complex>,
  orderItems: OrderItemMap,
  orderSettings: OrderSettings,
}

type Actions = {
  setComplexes: (ComplexMap) => void,
  addToOrder: (id: string, amount?: number) => void,
  initToOrder: (id: string, amount?: number) => void,
  resetOrder: () => void,
  incrementAmount: (string) => void,
  decrementAmount: (string) => void,
  removeFromOrder: (string) => void,
}

const deaultOrderState: OrderItemMap = new Map([]);
const deaultComplexesState: ComplexMap = new Map([]);

export default function useOrderCreate(
  available: Available,
  groupType: GroupType | null,
  onlySingleComplex?: boolean = false,
): [State, Actions] {
  const { t } = useTranslation('cafeteria');

  const lockControls = useRef(false);

  const [complexes, setComplexes] = useState<ComplexMap>(deaultComplexesState);
  const [orderItems, setOrderItems] = useState<OrderItemMap>(deaultOrderState);

  const orderSettings = useMemo(() => {
    const { dayCareOrderAvailable, klassOrderAvailable } = available;
    const balanceAvailable = groupType === groupTypes.DayCare ? dayCareOrderAvailable : klassOrderAvailable;
    const totalInOrder = reduce((acc, { cost, amount, freeCount }) => {
      return acc + cost * amount - cost * freeCount;
    }, 0, Array.from(orderItems.values()));

    return ({
      totalInOrder,
      balanceAvailable: Math.max((balanceAvailable || 0) - totalInOrder, 0),
    });
  }, [orderItems, available, groupType]);


  const { balanceAvailable } = orderSettings;


  const addToOrder = useCallback((itemId: string, amount?: number = 1) => {
    const targetComplex = complexes.get(itemId);
    if (targetComplex) {
      const { id, title, cost, freeCount } = targetComplex;
      if (cost !== null) {
        setOrderItems(state => new Map([
          ...Array.from(state.entries()),
          [itemId, createOrderItem({
            id,
            title,
            cost,
            freeCount,
            amount,
          })],
        ]));
      }
    }
  }, [complexes]);


  const initToOrder = useCallback((itemId: string, amount?: number = 1) => {
    const targetComplex = complexes.get(itemId);
    if (targetComplex) {
      const { id, title, cost, freeCount } = targetComplex;
      if (cost !== null) {
        setOrderItems(state => new Map([
          ...Array.from(state.entries()),
          [itemId, createOrderItem({
            id,
            title,
            cost,
            freeCount,
            amount,
          })],
        ]));
      }
    }
  }, [complexes]);


  const incrementAmount = useCallback((itemId: string) => {
    if (lockControls.current) return;
    lockControls.current = true;

    setOrderItems(state => new Map(
      map(
        when(
          propEq(0, itemId),
          over(lensPath(['1', 'amount']), inc),
        ),
        Array.from(state.entries()),
      ),
    ));
  }, []);


  const decrementAmount = useCallback((itemId: string) => {
    if (lockControls.current) return;
    lockControls.current = true;

    setOrderItems((ordersPrev) => {
      return new Map(
        map(
          when(
            propEq(0, itemId),
            over(lensPath(['1', 'amount']), dec),
          ),
          Array.from(ordersPrev.entries()),
        ),
      );
    });
  }, []);


  const removeFromOrder = useCallback((itemId: string) => {
    setOrderItems((ordersPrev) => {
      return new Map(
        filter(
          complement(propEq(0, itemId)),
          Array.from(ordersPrev.entries()),
        ),
      );
    });
  }, []);


  const resetOrder = useCallback(() => {
    setOrderItems(deaultOrderState);
  }, []);


  const complexesDependingOrder = useMemo(() => {
    const currentStateValues = Array.from(complexes.values());

    return map((props) => {
      const { id, cost, freeCount } = props;
      const orderItem = orderItems.get(id);
      const orderItemCount = orderItem ? orderItem.amount : 0;
      const freeCountCalculated = Math.max(0, freeCount - orderItemCount);
      const disabledReasonTranslateKey = getIsSourceItemDisabled({
        balanceAvailable,
        cost,
        freeCount: freeCountCalculated,
        noMoreAvailableToAdd: onlySingleComplex && orderItems.size > 0,
      });
      const disabled = !!disabledReasonTranslateKey;

      return ({
        ...props,
        freeCount: freeCountCalculated,
        disabled,
        disabledReason: t(disabledReasonTranslateKey),
      });
    }, currentStateValues);
  }, [complexes, balanceAvailable, orderItems, t, onlySingleComplex]);


  const orderItemsDependingOrder = useMemo(() => {
    const currentStateValues = Array.from(orderItems.values());

    return new Map(map((order) => {
      const { id, cost, freeCount, amount } = order;
      return ([id, {
        ...order,
        incrementEnabled: balanceAvailable >= cost || freeCount > amount,
      }]);
    }, currentStateValues));
  }, [orderItems, balanceAvailable]);


  useEffect(() => {
    lockControls.current = false;
  }, [orderItemsDependingOrder]);


  return [{
    complexes: complexesDependingOrder,
    orderItems: orderItemsDependingOrder,
    orderSettings,
  }, {
    setComplexes,
    addToOrder,
    initToOrder,
    resetOrder,
    incrementAmount,
    decrementAmount,
    removeFromOrder,
  }];
}
